// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — colour system + WCAG contrast helper (principle #22).
//
// The play surface is a grid of zones drawn on canvas OVER a faint, mirrored camera
// view. A live camera feed is arbitrary pixels, so contrast could be anything — UNLESS
// we bound it. The camera is drawn at a capped alpha (CAMERA_ALPHA, in render.ts) over
// the dark board, so the LIGHTEST a play-surface pixel can ever become is a single
// pure-white camera pixel composited through that alpha: PALETTE.bgWorst. Every colour
// that carries meaning (firefly, its core, the wasp, the motion pulse, the spark) is
// held to the 3:1 graphic floor against bgWorst — the worst case — and therefore
// against the (darker) solid board and panels too. bgWorst is a genuine analytic upper
// bound, not a guess: see the derivation in render.ts where the alpha is chosen so that
// white·α + board·(1−α) ≤ bgWorst in every channel.
//
// Colour is never the only cue: a firefly is a soft ROUND glow, a wasp is a spiky
// striped DIAMOND — told apart by shape, so colour-blindness is covered.

export const PALETTE = {
  // board + panels (dark night field)
  bg: '#0c1022',
  board: '#0c1022',
  panel: '#16233a',
  panelDeep: '#0e1828',
  edge: '#2b3d57',

  // the analytic worst-case play-surface background (white camera pixel through
  // CAMERA_ALPHA=0.18 over the board). Meaningful colours must clear 3:1 on THIS.
  bgWorst: '#3a3d4a',

  // text
  text: '#eef3f8',
  textMuted: '#9fb3c6',
  ink: '#1a1405', // dark glyph on a bright fill

  // play-surface roles (each also has a shape/behaviour cue)
  // (CAMERA_ALPHA is exported separately below — it is the load-bearing bound.)
  fly: '#ffd24a', // firefly body — a warm round glow
  flyCore: '#fff3c4', // firefly bright centre sheen
  wasp: '#ff6b57', // wasp — a spiky striped diamond (avoid it in Sting)
  pulse: '#6fe0ff', // the cyan flash when motion is sensed in a zone
  spark: '#ffe08a', // catch particles
} as const;

/**
 * The alpha the mirrored camera frame is drawn at over the board. Chosen so that even a
 * pure-white camera pixel composites to no lighter than PALETTE.bgWorst — see palette
 * header. Kept here (not in render.ts) so the contrast test can prove the bound without
 * importing the canvas renderer.
 */
export const CAMERA_ALPHA = 0.18;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Composite an opaque foreground over a background at alpha `a` → the resulting
 *  #rrggbb. Used to PROVE bgWorst is a real bound (white over board at CAMERA_ALPHA). */
export function composite(fg: string, bg: string, a: number): string {
  const parse = (h: string): [number, number, number] => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(h.trim())!;
    return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
  };
  const [fr, fg2, fb] = parse(fg);
  const [br, bg2, bb] = parse(bg);
  const mix = (f: number, b: number): number => Math.round(f * a + b * (1 - a));
  const hex = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${hex(mix(fr, br))}${hex(mix(fg2, bg2))}${hex(mix(fb, bb))}`;
}
